import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface GenerationStep {
  id: string;
  labelKey: string;
  icon: string;
  progress: number; // 0-100
}

const GENERATION_STEPS: GenerationStep[] = [
  { id: 'init', labelKey: 'generation.stepInit', icon: '🔍', progress: 5 },
  { id: 'world', labelKey: 'generation.stepWorld', icon: '🌍', progress: 20 },
  { id: 'intro', labelKey: 'generation.stepIntro', icon: '📜', progress: 35 },
  { id: 'locations', labelKey: 'generation.stepLocations', icon: '🏛️', progress: 55 },
  { id: 'npcs', labelKey: 'generation.stepNpcs', icon: '👥', progress: 75 },
  { id: 'roles', labelKey: 'generation.stepRoles', icon: '⚔️', progress: 88 },
  // Never reach 100% while still working — the bar stalls near the end so a slow (but healthy)
  // generation reads as "still loading", not "done" or "stuck". It only completes when the parent
  // unmounts this overlay on real success.
  { id: 'finalize', labelKey: 'generation.stepFinalize', icon: '✨', progress: 95 },
];

interface GenerationProgressProps {
  isGenerating: boolean;
  currentStep?: string;
  error?: string | null;
}

export default function GenerationProgress({ isGenerating, currentStep, error }: GenerationProgressProps) {
  const { t } = useTranslation();
  const [progress, setProgress] = useState(0);
  const [activeStepIndex, setActiveStepIndex] = useState(0);

  useEffect(() => {
    if (!isGenerating) {
      setProgress(0);
      setActiveStepIndex(0);
      return;
    }

    // If we have a specific step from backend
    if (currentStep) {
      const stepIndex = GENERATION_STEPS.findIndex(s => s.id === currentStep);
      if (stepIndex !== -1) {
        setActiveStepIndex(stepIndex);
        setProgress(GENERATION_STEPS[stepIndex].progress);
        return;
      }
    }

    // Otherwise, simulate progress
    let currentProgress = 0;
    let stepIndex = 0;

    const interval = setInterval(() => {
      if (stepIndex >= GENERATION_STEPS.length) {
        clearInterval(interval);
        return;
      }

      const targetProgress = GENERATION_STEPS[stepIndex].progress;

      if (currentProgress < targetProgress) {
        currentProgress += 1;
        setProgress(currentProgress);
      } else {
        stepIndex += 1;
        setActiveStepIndex(stepIndex);
      }
    }, 100);

    return () => clearInterval(interval);
  }, [isGenerating, currentStep]);

  if (!isGenerating) return null;

  const currentStepData = GENERATION_STEPS[activeStepIndex] || GENERATION_STEPS[0];
  // Safety net: never display 100% while generating (see note on GENERATION_STEPS).
  const displayProgress = Math.min(progress, 95);

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.9)',
      backdropFilter: 'blur(10px)',
      zIndex: 2000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      animation: 'fadeIn 0.3s ease-out',
    }}>
      <div style={{
        maxWidth: '600px',
        width: '90%',
        maxHeight: '92vh',
        overflowY: 'auto',
        background: 'rgba(15, 12, 41, 0.95)',
        borderRadius: '24px',
        border: '1px solid rgba(99, 102, 241, 0.3)',
        padding: '2rem 1.5rem',
        boxShadow: '0 30px 60px rgba(0, 0, 0, 0.5)',
      }}>
        {/* Main Icon */}
        <div style={{
          textAlign: 'center',
          marginBottom: '2rem',
        }}>
          <div style={{
            fontSize: '4rem',
            marginBottom: '1rem',
            animation: 'pulse 2s infinite',
          }}>
            {error ? '❌' : currentStepData.icon}
          </div>
          <h2 style={{
            fontSize: '2rem',
            margin: '0 0 0.5rem 0',
            background: error
              ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
              : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}>
            {error ? t('generation.error') : t('generation.creating')}
          </h2>
          {!error && (
            <p style={{
              fontSize: '1.1rem',
              color: '#a0aec0',
              margin: 0,
            }}>
              {t(currentStepData.labelKey)}
            </p>
          )}
          {error && (
            <p style={{
              fontSize: '0.95rem',
              color: '#fca5a5',
              margin: '1rem 0 0 0',
            }}>
              {error}
            </p>
          )}
        </div>

        {/* Prominent, high-up warning so it's seen without scrolling on small screens */}
        {!error && (
          <div style={{
            marginBottom: '1.5rem',
            padding: '0.85rem 1rem',
            borderRadius: '12px',
            background: 'rgba(245, 158, 11, 0.12)',
            border: '1px solid rgba(245, 158, 11, 0.4)',
            color: '#fcd34d',
            fontSize: '0.9rem',
            lineHeight: 1.5,
            textAlign: 'center',
          }}>
            ⚠️ {t('generation.dontLeave')}
          </div>
        )}

        {/* Progress Bar */}
        {!error && (
          <>
            <div style={{
              width: '100%',
              height: '12px',
              background: 'rgba(0, 0, 0, 0.3)',
              borderRadius: '10px',
              overflow: 'hidden',
              marginBottom: '2rem',
              border: '1px solid rgba(99, 102, 241, 0.2)',
            }}>
              <div style={{
                width: `${displayProgress}%`,
                height: '100%',
                background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                borderRadius: '10px',
                transition: 'width 0.3s ease-out',
                boxShadow: '0 0 20px rgba(102, 126, 234, 0.5)',
              }} />
            </div>

            {/* Progress Percentage */}
            <div style={{
              textAlign: 'center',
              fontSize: '1.5rem',
              fontWeight: 'bold',
              color: '#667eea',
              marginBottom: '2rem',
            }}>
              {displayProgress}%
            </div>

            {/* Steps List */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.75rem',
            }}>
              {GENERATION_STEPS.map((step, index) => {
                const isCompleted = index < activeStepIndex;
                const isCurrent = index === activeStepIndex;
                const isPending = index > activeStepIndex;

                return (
                  <div
                    key={step.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '1rem',
                      padding: '0.75rem 1rem',
                      background: isCurrent
                        ? 'rgba(99, 102, 241, 0.15)'
                        : 'rgba(99, 102, 241, 0.05)',
                      borderRadius: '12px',
                      border: `1px solid ${
                        isCurrent
                          ? 'rgba(99, 102, 241, 0.4)'
                          : 'rgba(99, 102, 241, 0.1)'
                      }`,
                      opacity: isPending ? 0.5 : 1,
                      transition: 'all 0.3s ease-out',
                    }}
                  >
                    <div style={{
                      fontSize: '1.5rem',
                      width: '32px',
                      textAlign: 'center',
                    }}>
                      {isCompleted ? '✅' : step.icon}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontSize: '0.95rem',
                        color: isCurrent ? '#e2e8f0' : '#cbd5e0',
                        fontWeight: isCurrent ? '600' : '400',
                      }}>
                        {t(step.labelKey)}
                      </div>
                    </div>
                    {isCurrent && (
                      <div style={{
                        fontSize: '1.2rem',
                        animation: 'spin 1s linear infinite',
                      }}>
                        ⚙️
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Loading Animation */}
        {!error && (
          <div style={{
            marginTop: '2rem',
            textAlign: 'center',
            fontSize: '0.85rem',
            color: '#718096',
          }}>
            <div style={{
              display: 'inline-flex',
              gap: '0.5rem',
              alignItems: 'center',
            }}>
              <span>{t('generation.aiProcessing')}</span>
              <span className="loading-dots">
                <span style={{ animation: 'blink 1.4s infinite both', animationDelay: '0s' }}>.</span>
                <span style={{ animation: 'blink 1.4s infinite both', animationDelay: '0.2s' }}>.</span>
                <span style={{ animation: 'blink 1.4s infinite both', animationDelay: '0.4s' }}>.</span>
              </span>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% {
            transform: scale(1);
            opacity: 1;
          }
          50% {
            transform: scale(1.1);
            opacity: 0.8;
          }
        }

        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        @keyframes blink {
          0%, 80%, 100% {
            opacity: 0;
          }
          40% {
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
}
